// src/components/LandingPage.js — redesigned marketing landing page.
// Visual targets: MOCKUP/LandingPage-Redesign.html (base) +
// MOCKUP/LandingTwoAppHoliday.html (increment 2: two-app hero, holiday-pay
// stacker, Features/Controls/FAQ polish — C9–C16, M6).
// See PRD/PRD_LandingPageRedesign.md. Pricing is sourced from
// src/config/plans.js (never hand-typed) so it can't drift from real billing (C4).
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Wordmark } from "./Wordmark";
import {
  BILLING_FLOOR,
  INCLUDED,
  BANDS,
  computeBillEstimate,
} from "../config/plans";
import {
  PHILIPPINE_LABOR_LAW_MINIMUMS,
  SPECIAL_HOLIDAY_REST_DAY_UPLIFT,
} from "../constants/laborLawMinimums";
import iconLight from "../assets/brand/sahod-icon-light.svg";
import iconDark from "../assets/brand/sahod-icon-dark.svg";
import Icon from "./icons/Icon";
import "../styles/sections/landing.css";

// Mirrors the dashboard formatter (BillingEstimateCard.js) so the marketing page,
// the in-app estimate, and Stripe all show the same number.
const peso = (n) => `₱${Number(n || 0).toLocaleString("en-PH")}`;

// Band employee-ranges derived once from the live config (C4) via a running
// accumulator, so the table and the FAQ prose can't disagree on a future BANDS
// edit (e.g. a reordered or non-final unbounded band). Each: { from, to, amount };
// `to == null` means unbounded.
const BAND_RANGES = (() => {
  let lower = INCLUDED;
  return BANDS.map((band) => {
    const range = { from: lower + 1, to: band.upTo, amount: band.amount };
    if (band.upTo != null) lower = band.upTo;
    return range;
  });
})();

const rangeLabel = (r) => (r.to == null ? `${r.from}+` : `${r.from}–${r.to}`);

// Pricing band rows for the pricing card — no number is hand-typed.
const BAND_ROWS = BAND_RANGES.map((r) => ({
  label: `Employees ${rangeLabel(r)}`,
  rate: `+${peso(r.amount)} each`,
}));

// Worked-example headcounts shown in the pricing panel.
const EXAMPLE_COUNTS = [INCLUDED, 25, 50, 75, 100];

const NAV_LINKS = [
  ["#features", "Features"],
  ["#governance", "Controls"],
  ["#compliance", "Compliance"],
  ["#pricing", "Pricing"],
  ["#faq", "FAQ"],
];

const PILLARS = [
  {
    icon: "clock",
    title: "Time & Attendance",
    lead: "Capture hours accurately, on your terms.",
    items: [
      ["Kiosk clock-in app", "Turn any phone or tablet into a shared time clock at your site — staff clock in with a PIN, nothing to install."],
      ["Manual entries & corrections", "Admins add or edit time records anytime — no kiosk required."],
      ["Attendance policy", "Your rules for grace periods, lates and undertime."],
    ],
  },
  {
    icon: "banknote",
    title: "Payroll & Pay",
    lead: "From hours to net pay, without missing a step.",
    items: [
      ["Multi-step payroll", "A guided run that walks you from review to net pay."],
      ["Statutory contributions", "Set SSS, PhilHealth, Pag-IBIG and BIR per employee — tracked and scheduled across pay periods."],
      ["Adjustments", "One-off additions and deductions, per employee."],
      ["Payslips", "Itemised payslips, single or in bulk, straight from every run."],
      ["Bank-ready export", "Configurable CSV you map once to your bank's layout, then export in one click."],
    ],
  },
  {
    icon: "users",
    title: "People & Insights",
    lead: "Know your workforce at a glance.",
    items: [
      ["Employee management", "Complete records with a guided onboarding wizard."],
      ["Employment history", "Track role and salary changes over time."],
      ["Dashboard analytics", "Attendance, pending approvals and payroll, at a glance."],
    ],
  },
];

// C14: maker-checker is a per-company setting, off by default (self-approval) —
// the copy says so, and the pillar carries an "Optional" tag.
const GOV_PILLARS = [
  { icon: "check-circle", title: "Maker-checker approvals", optional: true, desc: "Want a second set of eyes? Require a separate approver for overtime and payroll, so the preparer isn't the approver. Off by default — turn it on as your team grows." },
  { icon: "lock", title: "Role-based access", desc: "Custom roles with granular permissions — and salary, bank and government IDs locked to authorised roles only." },
  { icon: "file-pen", title: "Tamper-proof audit trail", desc: "Approvals and key changes are logged append-only — locked so the record can't be edited or deleted." },
  { icon: "building-2", title: "Isolated company data", desc: "Every company's data is sealed off at the database layer — so no other company can ever reach it." },
];

// The four agency deduction-tracking items live on the agency cards alongside —
// the checklist keeps only the premium-rate items (consistency trim).
const COMPLIANCE_ITEMS = [
  "Overtime pay (125% minimum)",
  "Holiday pay (regular + special)",
  "Night differential (10% premium)",
];

const AGENCIES = [
  ["sss", "SSS", "Social Security System"],
  ["phic", "PHIC", "PhilHealth"],
  ["hdmf", "HDMF", "Pag-IBIG Fund"],
  ["bir", "BIR", "Bureau of Internal Revenue"],
];

const STEPS = [
  ["calendar", "Discovery Session", "We learn how you run payroll today and check Sahod is the right fit."],
  ["lock", "Invite your team", "Add admins and HR staff and assign each a role."],
  ["users", "Add employees", "Onboard staff with the guided wizard."],
  ["clock", "Track time", "Use the kiosk app or manual entry, with automatic overtime."],
  ["wallet", "Run payroll", "Review, approve, generate payslips and export to your bank."],
];

const PLAN_FEATURES = [
  "Employee management",
  "Kiosk clock-in app",
  "Payroll & payslips",
  "Government deduction tracking",
  "Maker-checker approvals",
  "User roles & access",
  "Bank CSV export",
  "Dashboard analytics",
];

// C15: 9 items — cost + payment-methods removed (Pricing is the canonical
// answer; payment surfaces at checkout), clock-in + device-compat added
// (shared-station framing C16; version-free device copy per C3).
// PRD_DiscoverySession: + "Why a Discovery Session…" second → 10 items.
const FAQ_ITEMS = [
  {
    q: "What is Sahod.solutions?",
    a: "Sahod is a cloud-based payroll system built for Philippine businesses — employees, time tracking, approvals, payroll and payslips, plus SSS, PhilHealth, Pag-IBIG and BIR deduction tracking, all in one place.",
  },
  {
    q: "Why a Discovery Session instead of a sign-up button?",
    a: "Every company's payroll is different. We'd rather understand yours first — and if Sahod isn't the right fit, we'll tell you, and save you a trial you'd only cancel.",
  },
  {
    q: "How does the 14-day free trial work?",
    a: "After your Discovery Session, if Sahod is the right fit, we'll send you a link to set up your company. You then get full access to every feature for 14 days with no credit card required — add a payment method only when you're ready to continue.",
  },
  {
    q: "How do employees clock in?",
    a: "You set up the kiosk app (kiosk.sahod.solutions) on a shared phone or tablet at your workplace — a modern replacement for the bundy clock. Staff walk up and clock in with their ID and PIN; there's nothing for them to install. Admins can also add or edit time entries manually on the web app, so you're covered even without a kiosk.",
  },
  {
    q: "What devices does the kiosk work on?",
    a: "The kiosk runs in any modern web browser — there's nothing to install. It works on most phones and tablets, including older ones. For the smoothest experience, keep the device's browser (Chrome or Safari) up to date; very old devices that can't run a recent browser may not be supported.",
  },
  {
    q: "How do you count employees for billing?",
    a: "We take a daily snapshot of your active employees and bill for the highest number reached during the cycle. Counting the peak — rather than a single day — means everyone who was actually on your payroll that month is accounted for, and pricing stays consistent. You only ever pay for real, active employees, never pre-purchased seats.",
  },
  {
    q: "Can I have more than one admin, with different access?",
    a: "Yes. Add multiple admins and HR staff, each with their own custom role. Permissions are granular, so you control exactly what each teammate can view, edit or approve — and payroll and overtime can require a separate approver.",
  },
  {
    q: "Do you support payroll export to Philippine banks?",
    a: "Yes. Sahod exports a configurable CSV with all the payroll figures you need. You arrange the columns to match your bank's payroll file, then upload through your bank's own system. Set the layout once and reuse it every run.",
  },
  {
    q: "Is my employee data secure?",
    a: "Your data is stored securely with encryption in transit and at rest, and handled in line with the Data Privacy Act. See our Privacy Policy for details.",
  },
  {
    q: "Is there mobile access?",
    a: "Yes. Sahod is mobile-responsive, so you can manage employees, track time and check payroll from a tablet or phone. A full payroll run is doable on mobile but most comfortable on a desktop or laptop.",
  },
];

/* ---------------- Holiday-pay stacker (C10) ----------------
   Statutory-minimum stacking at the ₱1,000/day golden-test anchor. The
   composite multipliers are DERIVED from the six base minimums in
   laborLawMinimums.js by the same products computeEarnings.js uses to build
   its otRates — no composite is hand-typed (C4 discipline). The
   special-on-rest-day pair is the one case DOLE publishes as a FLAT figure
   rather than a product (day 1.5, OT 1.95 = 1.5 × restDayRate), so it derives
   from SPECIAL_HOLIDAY_REST_DAY_UPLIFT — the same constant the engine uses
   (BL-046). It lives OUTSIDE PHILIPPINE_LABOR_LAW_MINIMUMS.rates because that
   object feeds settings validation and this rate is not company-configurable.
   Displayed figures are statutory minimums, never engine output. */
const HP_DAILY = 1000;
const HP_HOURLY = 125;
const HP_OT_HOURS = 2;

const LAW = PHILIPPINE_LABOR_LAW_MINIMUMS.rates;
// Mirrors the engine's derivation exactly (functions/src/payroll/computeEarnings.js):
// the uplift sits over the HIGHER of the two component rates. At the statutory
// minimums this is max(1.3, 1.3) + 0.2 = 1.5.
const SPECIAL_REST_DAY_RATE =
  Math.max(LAW.specialHolidayRate, LAW.restDayRate) + SPECIAL_HOLIDAY_REST_DAY_UPLIFT;
const HP_RATES = {
  ordinary: { day: 1, ot: LAW.overtimeRate, tag: "Ordinary day" },
  // ot is the statutory rest-day OT, restDayRate² (1.69). computeEarnings pays
  // max(restDayOvertimeRate, restDayRate²), which at these legal minimums is the
  // same 1.69 (LAW.restDayOvertimeRate).
  ordinaryRest: { day: LAW.restDayRate, ot: LAW.restDayRate * LAW.restDayRate, tag: "Rest day" },
  regular: {
    day: LAW.regularHolidayRate,
    ot: LAW.regularHolidayRate * LAW.restDayRate,
    tag: "Regular holiday",
  },
  regularRest: {
    day: LAW.regularHolidayRate * LAW.restDayRate,
    ot: LAW.regularHolidayRate * LAW.restDayRate * LAW.restDayRate,
    tag: "Regular holiday · rest day",
  },
  special: {
    day: LAW.specialHolidayRate,
    ot: LAW.specialHolidayRate * LAW.restDayRate,
    tag: "Special non-working day",
  },
  specialRest: {
    day: SPECIAL_REST_DAY_RATE,
    ot: SPECIAL_REST_DAY_RATE * LAW.restDayRate,
    tag: "Special day · rest day",
  },
};

// 2-decimal peso for the widget (the shared `peso` keeps whole pesos for pricing).
const peso2 = (n) =>
  `₱${Number(n).toLocaleString("en-PH", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

// "×2.6" labels from the derived multipliers, rounded so float products
// (e.g. 2.0 × 1.3 × 1.3) never leak digits into the copy.
const multX = (m) => `×${Math.round(m * 100) / 100}`;

// An ordinary 8h day + 2h OT — the "vs" comparison base.
const HP_ORD_OT = HP_DAILY + HP_OT_HOURS * HP_HOURLY * LAW.overtimeRate;

// Tween only for users who are fine with motion; in jsdom (no matchMedia)
// and under prefers-reduced-motion the number just snaps to the final value.
const hpAllowMotion = () =>
  typeof window.matchMedia === "function" &&
  !window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const hpWorkedKey = (day, rest) => {
  if (day === "ordinary") return rest ? "ordinaryRest" : "ordinary";
  if (day === "regular") return rest ? "regularRest" : "regular";
  return rest ? "specialRest" : "special";
};

// One result card driven by all the toggles: "Did they work it?" switches the
// whole card, so Present/Absent moves the headline Day's Pay (regular holiday:
// ₱1,000 present → ₱0 absent) — never an isolated side panel.
const hpScenario = ({ day, worked, rest, ot, present, night }) => {
  if (worked) {
    const r = HP_RATES[hpWorkedKey(day, rest)];
    const dayPay = HP_DAILY * r.day;
    const otPay = ot ? HP_OT_HOURS * HP_HOURLY * r.ot : 0;
    // PRD_ShiftRules C7: the 10% on the 8 regular hours at the day's rate. A 10pm–6am
    // shift's 2 OT hours fall at 6–8am, outside the night window, so they earn none.
    const nightPay = night ? HP_DAILY * r.day * LAW.nightDiffRate : 0;
    const rows = [{ label: "Base daily rate", amt: peso2(HP_DAILY) }];
    if (day === "regular") {
      rows.push({
        label: "Regular holiday pay",
        mult: multX(HP_RATES.regular.day),
        amt: `+ ${peso2(HP_DAILY * (HP_RATES.regular.day - 1))}`,
        add: true,
      });
      if (rest) {
        rows.push({
          label: "Falls on their rest day",
          mult: `${multX(HP_RATES.regularRest.day)} total`,
          amt: `+ ${peso2(HP_DAILY * (HP_RATES.regularRest.day - HP_RATES.regular.day))}`,
          add: true,
        });
      }
    } else if (day === "special") {
      if (rest) {
        rows.push({
          label: "Special day on a rest day",
          mult: multX(HP_RATES.specialRest.day),
          amt: `+ ${peso2(HP_DAILY * (HP_RATES.specialRest.day - 1))}`,
          add: true,
          sub: "+50% rule",
        });
      } else {
        rows.push({
          label: "Special non-working day",
          mult: multX(HP_RATES.special.day),
          amt: `+ ${peso2(HP_DAILY * (HP_RATES.special.day - 1))}`,
          add: true,
        });
      }
    } else if (rest) {
      rows.push({
        label: "Worked on their rest day",
        mult: multX(HP_RATES.ordinaryRest.day),
        amt: `+ ${peso2(HP_DAILY * (HP_RATES.ordinaryRest.day - 1))}`,
        add: true,
      });
    }
    if (ot) {
      rows.push({
        label: `Overtime · 2h at ${peso2(HP_HOURLY)}/h`,
        mult: multX(r.ot),
        amt: `+ ${peso2(otPay)}`,
        add: true,
      });
    }
    if (night) {
      rows.push({
        label: "Night differential",
        mult: `+${Math.round(LAW.nightDiffRate * 100)}%`,
        amt: `+ ${peso2(nightPay)}`,
        add: true,
      });
    }
    const total = dayPay + otPay + nightPay;
    const ordComp = ot ? HP_ORD_OT : HP_DAILY;
    const m = total / ordComp;
    return {
      scenario: "If they worked it",
      tag: r.tag,
      rows,
      total,
      mult: m > 1.001 ? `${m.toFixed(1)}× a normal day` : null,
      vs: m > 1.001 ? `vs ${peso2(ordComp)} on an ordinary day` : "a normal 8-hour day",
      warn: false,
    };
  }
  const scenario = "If they didn't work it";
  if (day === "regular" && present) {
    return {
      scenario,
      tag: "Regular holiday · unworked",
      rows: [{
        label: "Holiday pay — present the workday before",
        mult: "100% · unworked",
        amt: peso2(HP_DAILY),
        add: true,
      }],
      total: HP_DAILY,
      mult: null,
      vs: `Paid in full though unworked · absent the day before → ${peso2(0)}`,
      warn: false,
    };
  }
  if (day === "regular") {
    return {
      scenario,
      tag: "Regular holiday · unworked",
      rows: [{ label: "Not entitled — absent the workday before", amt: peso2(0) }],
      total: 0,
      mult: null,
      vs: `Present the day before → ${peso2(HP_DAILY)}`,
      warn: true,
    };
  }
  if (day === "special") {
    return {
      scenario,
      tag: "Special non-working · unworked",
      rows: [{
        label: "No work, no pay",
        amt: peso2(0),
        sub: "special days aren't paid when unworked",
      }],
      total: 0,
      mult: null,
      vs: "A regular holiday would still pay — a special day does not",
      warn: false,
    };
  }
  return {
    scenario,
    tag: "Ordinary day · unworked",
    rows: [{
      label: "No work, no pay",
      amt: peso2(0),
      sub: "an ordinary day isn't a holiday",
    }],
    total: 0,
    mult: null,
    vs: "",
    warn: false,
  };
};

const HolidayPayStacker = () => {
  const [day, setDay] = useState("regular");
  const [worked, setWorked] = useState(true);
  const [rest, setRest] = useState(true);
  const [ot, setOt] = useState(true);
  const [present, setPresent] = useState(true);
  const [night, setNight] = useState(false);

  const result = hpScenario({ day, worked, rest, ot, present, night });
  const { total } = result;

  // Count-up tween + scale bump on the headline number so each toggle feels
  // like the premiums stacking. React renders the final value; the tween
  // re-plays the transition over the same node and lands on that exact text.
  const totalRef = useRef(null);
  const prevTotal = useRef(total);
  useEffect(() => {
    const from = prevTotal.current;
    prevTotal.current = total;
    const el = totalRef.current;
    if (from === total || !el) return undefined;
    if (!hpAllowMotion()) {
      // Write through the same path as the tween: once a tween has replaced
      // the text node, React's own text commits go to the detached old node,
      // so a plain render can't be trusted to reach the screen.
      el.textContent = peso2(total);
      return undefined;
    }
    el.classList.remove("bump");
    void el.offsetWidth; // restart the bump animation
    el.classList.add("bump");
    const dur = 420;
    const start = performance.now();
    let raf;
    const frame = (now) => {
      const t = Math.min(1, (now - start) / dur);
      const eased = 1 - (1 - t) ** 3;
      const value = t < 1 ? from + (total - from) * eased : total;
      prevTotal.current = value; // an interrupted tween resumes from the screen value
      el.textContent = peso2(value);
      if (t < 1) raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
  }, [total]);

  const dayBtn = (key, label, eg) => (
    <button
      type="button"
      className="hp-toggle"
      aria-pressed={day === key}
      onClick={() => setDay(key)}
    >
      <span className="dot" aria-hidden="true" />
      <span className="nm">{label}</span>
      {eg && <span className="eg">{eg}</span>}
    </button>
  );

  return (
    <div className="hp">
      <div className="hp__controls">
        <div className="grp">
          <div className="grp-label">What day is it?</div>
          <div className="hp-seg" role="group" aria-label="Day type">
            {dayBtn("ordinary", "Ordinary day")}
            {dayBtn("regular", "Regular holiday", "e.g. Christmas Day")}
            {dayBtn("special", "Special non-working day", "e.g. All Saints' Day")}
          </div>
        </div>
        <div className="grp">
          <div className="grp-label">Did they work it?</div>
          <div className="hp-seg hp-seg--2" role="group" aria-label="Worked the day">
            <button type="button" className="hp-toggle" aria-pressed={worked} onClick={() => setWorked(true)}>
              <span className="dot" aria-hidden="true" /><span>Worked it</span>
            </button>
            <button type="button" className="hp-toggle" aria-pressed={!worked} onClick={() => setWorked(false)}>
              <span className="dot" aria-hidden="true" /><span>Didn't work it</span>
            </button>
          </div>
        </div>
        {worked && (
          <div className="grp">
            <div className="grp-label">And it also happens to be…</div>
            <div className="hp-seg">
              <button type="button" className="hp-toggle" aria-pressed={rest} onClick={() => setRest((v) => !v)}>
                <span className="dot" aria-hidden="true" /><span>The employee's rest day</span>
              </button>
              <button type="button" className="hp-toggle" aria-pressed={ot} onClick={() => setOt((v) => !v)}>
                <span className="dot" aria-hidden="true" /><span>A day they worked 2 hours overtime</span>
              </button>
              <button type="button" className="hp-toggle" aria-pressed={night} onClick={() => setNight((v) => !v)}>
                <span className="dot" aria-hidden="true" /><span>A night shift (10pm–6am)</span>
              </button>
            </div>
          </div>
        )}
        {!worked && day === "regular" && (
          <div className="grp">
            <div className="grp-label">Present the workday before?</div>
            <div className="hp-seg hp-seg--2" role="group" aria-label="Present the workday before the holiday">
              <button type="button" className="hp-toggle" aria-pressed={present} onClick={() => setPresent(true)}>
                <span className="dot" aria-hidden="true" /><span>Present</span>
              </button>
              <button type="button" className="hp-toggle" aria-pressed={!present} onClick={() => setPresent(false)}>
                <span className="dot" aria-hidden="true" /><span>Absent</span>
              </button>
            </div>
            <p className="hp__elig-hint">Present = worked <em>or</em> on paid leave that day.</p>
          </div>
        )}
        <p className="hp__note">
          Example: 8-hour day at a <b>{peso2(HP_DAILY)}</b> daily rate
          ({peso2(HP_HOURLY)}/hour), statutory minimum rates.
        </p>
      </div>

      <div>
        <div className="hp__card">
          <div className="hp__card-head">
            <span className="t">{result.scenario}</span>
            <span className="hp__daytag">{result.tag}</span>
          </div>
          <div className="hp__rows">
            {result.rows.map((row) => (
              <div className="hp-row" key={row.label}>
                <span className="lab">
                  {row.label}
                  {row.mult && <span className="mult tnum">{row.mult}</span>}
                  {row.sub && <small>{row.sub}</small>}
                </span>
                <span className={`amt tnum${row.add ? " add" : ""}`}>{row.amt}</span>
              </div>
            ))}
          </div>
          <div className="hp__total">
            <span className="lab">Day's pay<small>{result.vs}</small></span>
            <span className="hp__totright">
              {result.mult && <span className="hp__mult" data-testid="hp-mult">{result.mult}</span>}
              <span
                className={`amt tnum${total === 0 ? " zero" : ""}`}
                data-testid="hp-total"
                ref={totalRef}
              >
                {peso2(total)}
              </span>
            </span>
          </div>
          {result.warn && (
            <div className="hp__warn">
              <span aria-hidden="true"><Icon name="alert-triangle" size={16} /></span>
              <span>
                Absent the workday before a regular holiday means <b>no holiday pay</b> —
                the day-before rule most manual payrolls miss. Sahod flags it automatically.
              </span>
            </div>
          )}
        </div>
        <p className="hp__fear">Get a premium wrong and it's an underpaid employee — or a complaint down the line.</p>
        <p className="hp__closer">
          <span className="ck" aria-hidden="true"><Icon name="check" size={13} /></span> Sahod computes every layer from
          real clock-ins, at statutory minimum rates — every cutoff.
        </p>
      </div>
    </div>
  );
};

export const LandingPage = () => {
  const [activeFaq, setActiveFaq] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <div className="landing">
      {/* NAV */}
      <nav className="nav">
        <div className="lp-container nav__inner">
          <Link to="/" className="brand">
            <img className="brand__logo" src={iconLight} alt="" />
            <Wordmark />
          </Link>

          <div className="nav__links">
            {NAV_LINKS.map(([href, label]) => (
              <a key={href} href={href}>{label}</a>
            ))}
          </div>

          <div className="nav__actions">
            <Link to="/login" className="lp-btn lp-btn--outline">Log In</Link>
            <Link to="/discovery" className="lp-btn lp-btn--primary">Book a Discovery Session</Link>
          </div>

          <button
            type="button"
            className="nav__toggle"
            aria-label="Toggle menu"
            aria-expanded={mobileOpen}
            aria-controls="landing-mobile-menu"
            onClick={() => setMobileOpen((open) => !open)}
          >
            <span />
            <span />
            <span />
          </button>
        </div>

        <div
          id="landing-mobile-menu"
          className={`nav__mobile${mobileOpen ? " open" : ""}`}
        >
          {NAV_LINKS.map(([href, label]) => (
            <a key={href} href={href} onClick={() => setMobileOpen(false)}>{label}</a>
          ))}
          <Link to="/login" className="lp-btn lp-btn--outline lp-btn--full">Log In</Link>
          <Link to="/discovery" className="lp-btn lp-btn--primary lp-btn--full">Book a Discovery Session</Link>
        </div>
      </nav>

      {/* HERO */}
      <header className="lp-hero">
        <div className="lp-container lp-hero__grid">
          <div>
            <div className="lp-hero__badge">Proudly Filipino. Built for Philippine SMEs.</div>
            <h1>
              Run Philippine payroll <em>from clock-in to bank file</em>
            </h1>
            <p className="lp-hero__sub">
              <b>Two apps, one platform.</b> Employees clock in on the kiosk
              app<sup className="fn">*</sup>; you run payroll on the web — SSS,
              PhilHealth, Pag-IBIG, payslips and bank-ready exports in minutes,
              not days.
            </p>
            <div className="lp-hero__cta">
              <Link to="/discovery" className="lp-btn lp-btn--white lp-btn--lg">Book a Discovery Session</Link>
              <a href="#how-it-works" className="lp-btn lp-btn--ghost lp-btn--lg">See how it works</a>
            </div>
            <div className="lp-hero__noteline">
              <Icon name="check" size={14} aria-hidden="true" /> <b>30 minutes on Google Meet</b> · an honest answer on whether Sahod fits
            </div>
            <div className="lp-hero__trust">
              <span><i><Icon name="check" size={12} /></i> Field-level data protection</span>
              <span><i><Icon name="check" size={12} /></i> Built-in approval controls</span>
              <span><i><Icon name="check" size={12} /></i> Complete audit trail</span>
            </div>
            <div className="lp-hero__fn">* No kiosk? Admins can enter and edit time manually too.</div>
          </div>

          <div className="preview">
            <div className="float float--1"><i><Icon name="wallet" size={16} /></i> Gov't Deductions</div>
            <div className="float float--2"><i><Icon name="lock" size={16} /></i> Data Protected</div>
            <div className="win">
              <div className="win__bar">
                <i className="dot-r" /><i className="dot-y" /><i className="dot-g" />
              </div>
              <div className="win__body">
                <div className="pulse">
                  <div className="pulse__t">Today's Pulse</div>
                  <div className="pulse__row">
                    <div><div className="v">Jun 15</div><div className="l">Date</div></div>
                    <div><div className="v">50/52</div><div className="l">Active</div></div>
                    <div><div className="v">2</div><div className="l">On Leave</div></div>
                    <div><div className="v gold">₱684K</div><div className="l">Est. Gross</div></div>
                  </div>
                </div>
                <div className="cards3">
                  <div className="minic"><div className="h">Approvals</div><div className="v s">3</div><div className="l">Awaiting checker</div></div>
                  <div className="minic"><div className="h">Workforce</div><div className="v">52</div><div className="l">Employees</div></div>
                  <div className="minic"><div className="h">Last payroll</div><div className="v gold">₱583K</div><div className="l">Net pay</div></div>
                </div>
              </div>
            </div>

            {/* Employee kiosk device (C9) — decorative, inert; fields mirror the
                live kiosk app (see MOCKUP/kiosk-app.html). Sits lower-right so it
                clears the top-right badge; both floats keep their corners. */}
            <div className="phone" aria-hidden="true">
              <div className="phone__screen">
                <div className="ph-head">
                  <div className="ph-brand">
                    <div className="ph-mark">S</div>
                    <div className="ph-co">Building A<small>Time Clock</small></div>
                  </div>
                  <div className="ph-clock">
                    <div className="ph-t">2:45 PM<i className="ph-dot" /></div>
                    <div className="ph-d">Tue, Jun 15</div>
                  </div>
                </div>
                <div className="ph-body">
                  <div className="ph-field"><span className="lab">Employee ID</span><span className="val tnum">0042</span></div>
                  <div className="ph-field active"><span className="lab">PIN</span><span className="ph-pins"><i className="on" /><i className="on" /><i className="on" /><i /></span></div>
                  <div className="ph-pad">
                    <div className="ph-key tnum">1</div><div className="ph-key tnum">2</div><div className="ph-key tnum">3</div>
                    <div className="ph-key tnum">4</div><div className="ph-key tnum">5</div><div className="ph-key tnum">6</div>
                    <div className="ph-key tnum">7</div><div className="ph-key tnum">8</div><div className="ph-key tnum">9</div>
                    <div className="ph-key util">Clear</div><div className="ph-key tnum">0</div><div className="ph-key util"><Icon name="delete" size={16} /></div>
                  </div>
                  <div className="ph-actions">
                    <div className="ph-btn in">Clock In</div>
                    <div className="ph-btn out">Clock Out</div>
                  </div>
                  <div className="ph-changepin">Change my PIN</div>
                </div>
              </div>
              <div className="phone__tag"><Icon name="chevron-right" size={12} aria-hidden="true" /> Try the kiosk — <b>kiosk.sahod.solutions</b></div>
            </div>
          </div>
        </div>
      </header>

      {/* FEATURES */}
      <section className="lp-section" id="features">
        <div className="lp-container">
          <div className="head">
            <div className="rule" />
            <h2>Everything payroll, in one place</h2>
            <p>Time, pay and people — built around the way Filipino companies actually run payroll.</p>
          </div>
          <div className="themes">
            {PILLARS.map((pillar) => (
              <div className="theme" key={pillar.title}>
                <div className="theme__ic" aria-hidden="true"><Icon name={pillar.icon} size={26} /></div>
                <h3>{pillar.title}</h3>
                <p className="theme__lead">{pillar.lead}</p>
                <ul>
                  {pillar.items.map(([name, desc]) => (
                    <li key={name}>
                      <span className="ck" aria-hidden="true"><Icon name="check" size={13} /></span>
                      <span><b>{name}</b>{desc}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOLIDAY-PAY STACKER (C10) — own id, no nav item */}
      <section className="lp-section lp-section--gray" id="holiday-pay">
        <div className="lp-container">
          <div className="head head--wide">
            <div className="rule" />
            <h2>How much is one holiday shift worth?</h2>
            <p>
              Regular holiday? Rest day too? Overtime on top? The premiums compound
              fast — toggle a shift and watch. This is exactly what your payroll has
              to get right, every cutoff.
            </p>
          </div>
          <HolidayPayStacker />
        </div>
      </section>

      {/* GOVERNANCE / CONTROLS */}
      <section className="lp-section" id="governance">
        <div className="lp-container">
          <div className="gov">
            <div>
              <div className="gov__badge"><Icon name="scale" size={16} aria-hidden="true" /> Controls &amp; governance</div>
              <h2>Controls that scale with your team</h2>
              <p>Payroll is sensitive. Switch on the controls you need as you grow — or keep it simple when it's just you.</p>
            </div>
            <div className="gov__pillars">
              {GOV_PILLARS.map(({ icon, title, optional, desc }) => (
                <div className="pillar" key={title}>
                  <div className="pillar__ic" aria-hidden="true"><Icon name={icon} size={22} /></div>
                  <div>
                    <h4>
                      {title}
                      {optional && <span className="pillar__opt">Optional</span>}
                    </h4>
                    <p>{desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* COMPLIANCE */}
      <section className="lp-section lp-section--tint" id="compliance">
        <div className="lp-container comp">
          <div>
            <div className="comp__badge"><Icon name="check" size={14} aria-hidden="true" /> Built for Philippine businesses</div>
            <h2>Designed for Philippine payroll</h2>
            <p>From statutory premium rates to organised government deduction tracking — the realities of PH payroll, handled.</p>
            <ul className="checklist">
              {COMPLIANCE_ITEMS.map((item) => (
                <li key={item}><span className="ck" aria-hidden="true"><Icon name="check" size={13} /></span> {item}</li>
              ))}
            </ul>
            <Link to="/discovery" className="lp-btn lp-btn--primary lp-btn--lg">Book a Discovery Session</Link>
          </div>
          <div>
            <div className="agency">
              {AGENCIES.map(([key, code, name]) => (
                <div className={`acard acard--${key}`} key={key}>
                  <div className="acard__logo">{code}</div>
                  <div className="acard__name">{name}</div>
                  <div className="acard__status"><Icon name="check" size={13} aria-hidden="true" /> Tracked</div>
                </div>
              ))}
            </div>
            <div className="dpa">
              <i aria-hidden="true"><Icon name="shield-check" size={20} /></i>
              <div>
                <b>Your data, protected</b>
                <span>Handled in line with the Data Privacy Act — see our <Link to="/privacy">Privacy Policy</Link>.</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="lp-section" id="how-it-works">
        <div className="lp-container">
          <div className="head">
            <div className="rule" />
            <h2>Get started in 5 simple steps</h2>
          </div>
          <div className="steps">
            {STEPS.map(([icon, title, desc]) => (
              <div className="step" key={title}>
                <div className="step__n" aria-hidden="true"><Icon name={icon} size={28} /></div>
                <h3>{title}</h3>
                <p>{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section className="lp-section lp-section--gray" id="pricing">
        <div className="lp-container">
          <div className="head">
            <div className="rule" />
            <h2>Simple, transparent pricing</h2>
            <p>One plan, every feature included. Your 14-day free trial starts after your Discovery Session — no credit card required.</p>
          </div>
          <div className="lp-pricing">
            <div className="pcard">
              <div className="pcard__ribbon">14-day free trial</div>
              <h3>Pay as you grow</h3>
              <div className="pcard__tag">All features included, always.</div>
              <div className="pcard__soft">
                <span className="pcard__flag" aria-hidden="true">Soft launch</span>
                <div className="price">
                  <span className="amt">{peso(BILLING_FLOOR)}</span>
                  <span className="per">/month</span>
                </div>
                <div className="pcard__softnote"><b>Our launch rate</b> while we're new — prices rise from here.</div>
              </div>
              <div className="price__sub">Flat base fee — covers your first <b>{INCLUDED} employees</b>.</div>
              <ul className="bands">
                <li>
                  <span className="base">Base — up to {INCLUDED} employees</span>
                  <span className="base">{peso(BILLING_FLOOR)}/mo</span>
                </li>
                {BAND_ROWS.map((row) => (
                  <li key={row.label}>
                    <span>{row.label}</span>
                    <span>{row.rate}</span>
                  </li>
                ))}
              </ul>
              <ul className="pfeat">
                {PLAN_FEATURES.map((feature) => (
                  <li key={feature}><span className="ck" aria-hidden="true"><Icon name="check" size={13} /></span> {feature}</li>
                ))}
              </ul>
              <Link to="/discovery" className="lp-btn lp-btn--primary lp-btn--lg lp-btn--full">Book a Discovery Session</Link>
            </div>

            <div className="examples">
              <h4>What you'd pay each month</h4>
              <div className="examples__hint">Base {peso(BILLING_FLOOR)} (first {INCLUDED}) plus graduated per-employee pricing above {INCLUDED}.</div>
              <div className="extable">
                {EXAMPLE_COUNTS.map((n) => (
                  <div className="exrow" key={n}>
                    <span className="sz">
                      {n <= INCLUDED ? `Up to ${INCLUDED} employees` : `${n} employees`}
                    </span>
                    <span className="pr" data-testid={`price-${n}`}>
                      {peso(computeBillEstimate(n).total)}
                    </span>
                  </div>
                ))}
              </div>
              <div className="examples__note">Billed monthly in advance. Cancel anytime — service runs to the end of the paid cycle.</div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="lp-section" id="faq">
        <div className="lp-container">
          <div className="head">
            <div className="rule" />
            <h2>Frequently asked questions</h2>
          </div>
          <div className="faq">
            {FAQ_ITEMS.map((item, i) => {
              const open = activeFaq === i;
              return (
                <div className={`qa${open ? " open" : ""}`} key={item.q}>
                  <button
                    type="button"
                    className="qa__q"
                    data-testid="faq-question"
                    aria-expanded={open}
                    onClick={() => setActiveFaq(open ? null : i)}
                  >
                    {item.q}
                    <span className="chev" aria-hidden="true"><Icon name="chevron-down" size={14} /></span>
                  </button>
                  <div className="qa__a"><div>{item.a}</div></div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="final">
        <div className="lp-container">
          <h2>Not sure Sahod fits? Let's find out together.</h2>
          <p>Book a 30-minute Discovery Session — we'll learn how you run payroll today and tell you honestly if Sahod is right for you.</p>
          <div className="final__cta">
            <Link to="/discovery" className="lp-btn lp-btn--white lp-btn--lg">Book a Discovery Session</Link>
            <Link to="/login" className="lp-btn lp-btn--ghost lp-btn--lg">Log In</Link>
          </div>
          <div className="final__note">No sales pitch. No credit card.</div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="foot">
        <div className="foot__stripe" />
        <div className="lp-container">
          <div className="foot__grid">
            <div className="foot__brand">
              <div className="brand">
                <img className="brand__logo" src={iconDark} alt="" />
                <Wordmark />
              </div>
              <p>Philippine payroll made simple — time tracking, approvals, payslips and bank-ready exports in one platform.</p>
            </div>
            <div>
              <h4>Product</h4>
              <ul>
                <li><a href="#features">Features</a></li>
                <li><a href="#governance">Controls</a></li>
                <li><a href="#pricing">Pricing</a></li>
                <li><a href="#compliance">Compliance</a></li>
              </ul>
            </div>
            <div>
              <h4>Company</h4>
              <ul>
                <li><a href="mailto:support@sahod.solutions">Contact</a></li>
              </ul>
            </div>
            <div>
              <h4>Legal</h4>
              <ul>
                <li><Link to="/privacy">Privacy Policy</Link></li>
                <li><Link to="/terms">Terms of Service</Link></li>
              </ul>
            </div>
          </div>
          <div className="foot__bottom">
            <span>© 2026 Sahod.solutions. All rights reserved.</span>
          </div>
        </div>
      </footer>
    </div>
  );
};
